import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { FormModal } from '../../../ui-kit';
import './_change-payment-method-modal.scss';

const formatAmount = (n) =>
  Number(n || 0).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });

/**
 * "Change payment method" picker — opened from the Payment Method card in
 * the PaymentsInvoice modal. Two options per Figma:
 *   - Pay with Shootsta Dollars (shows current balance)
 *   - Bill to my organisation (existing invoice flow)
 *
 * Clicking an option applies it immediately and closes the picker. Back
 * link / X close = cancel without changing the current selection.
 *
 * Per spec §5.2 (#3429 / #3430): designed to accept additional methods
 * without structural changes — extend `PAYMENT_METHODS` and the parent's
 * `onConfirm` handler.
 */
function ChangePaymentMethodModal({
  visible,
  onClose,
  onConfirm,
  selectedMethod,
  dollarsRemaining = 0,
  currencySymbol = '$'
}) {
  const handleSelect = (method) => {
    onConfirm?.(method);
    onClose?.();
  };

  const options = [
    {
      id: 'dollars',
      label: 'Pay with Shootsta Dollars',
      description: `Balance: ${currencySymbol}${formatAmount(dollarsRemaining)}`
    },
    {
      id: 'invoice',
      label: 'Bill to my organisation',
      description:
        'Outstanding amounts will be billed to your organisation at the end of the month.'
    }
  ];

  return (
    <div className="change-payment-method-modal">
      <FormModal
        className="change-payment-method-modal__form"
        visible={visible}
        onClose={onClose}
        title=""
        hideFooter
        minWidth="0"
        isAutoWidth
      >
        <div className="change-payment-method-modal__body">
          <button
            type="button"
            onClick={onClose}
            className="change-payment-method-modal__back"
          >
            <ArrowLeft size={18} strokeWidth={2.25} />
            <span>Back</span>
          </button>

          <div className="change-payment-method-modal__header">
            <div className="change-payment-method-modal__header__title">
              Change payment method
            </div>
            <div className="change-payment-method-modal__header__subtitle">
              Choose how you&apos;d like to handle the add-on costs.
            </div>
          </div>

          <div
            className="change-payment-method-modal__options"
            role="radiogroup"
            aria-label="Payment methods"
          >
            {options.map(({ id, label, description }) => {
              const isSelected = selectedMethod === id;
              const handleKey = (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleSelect(id);
                }
              };
              return (
                <div
                  key={id}
                  role="radio"
                  tabIndex={0}
                  aria-checked={isSelected}
                  onClick={() => handleSelect(id)}
                  onKeyDown={handleKey}
                  className={[
                    'change-payment-method-modal__option',
                    isSelected
                      ? 'change-payment-method-modal__option--selected'
                      : ''
                  ].join(' ')}
                >
                  <span
                    className={[
                      'change-payment-method-modal__option__radio',
                      isSelected
                        ? 'change-payment-method-modal__option__radio--checked'
                        : ''
                    ].join(' ')}
                    aria-hidden="true"
                  />
                  <div className="change-payment-method-modal__option__text">
                    <div className="change-payment-method-modal__option__text__label">
                      {label}
                    </div>
                    <div className="change-payment-method-modal__option__text__description">
                      {description}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </FormModal>
    </div>
  );
}

export default ChangePaymentMethodModal;
